import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { 
  Box, 
  Button, 
  CircularProgress,
  Grid, 
  IconButton,
  InputAdornment,
  TextField, 
  Typography,
  styled,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper
} from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from "../../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from 'react-toastify';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    padding: theme.spacing(2),
    minWidth: '600px',
    maxWidth: '700px',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

interface PaymentHeader {
  id: string;
  name: string;
  defaultAmount?: number;
  description?: string;
}

interface HeaderFormData {
  name: string;
  defaultAmount: string;
  description: string;
}

export default function PaymentHeadersModal() {
  const open = useAppSelector((state: RootState) => state.ModalSlice.PaymentHeadersModalOpen);
  const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
  const serviceProviderId = useAppSelector(selectEffectiveUID);
  const dispatch = useAppDispatch();

  const [headers, setHeaders] = useState<PaymentHeader[]>([]);
  const [editingHeader, setEditingHeader] = useState<PaymentHeader | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<HeaderFormData>({
    name: '',
    defaultAmount: '',
    description: ''
  });

  // Load existing headers
  useEffect(() => {
    if (open && serviceProviderId) {
      loadHeaders();
    }
  }, [open, serviceProviderId]);

  const loadHeaders = async () => {
    try {
      const spDoc = await getDoc(doc(db, 'ServiceProviders', serviceProviderId));
      if (spDoc.exists()) {
        const data = spDoc.data();
        setHeaders(data.paymentHeaders || []);
      }
    } catch (error) {
      console.error('Error loading headers:', error);
      toast.error('Failed to load payment categories');
    }
  };

  const handleClose = () => {
    dispatch(closeModal("PaymentHeadersModalOpen"));
    setShowForm(false);
    setEditingHeader(null);
    setFormData({ name: '', defaultAmount: '', description: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    // Check for duplicate names
    const isDuplicate = headers.some(header => 
      header.name.toLowerCase() === formData.name.toLowerCase() && 
      (!editingHeader || header.id !== editingHeader.id)
    );

    if (isDuplicate) {
      toast.error("A category with this name already exists");
      return;
    }

    try {
      dispatch(toggleLoading(true));

      const newHeader: PaymentHeader = {
        id: editingHeader?.id || Date.now().toString(),
        name: formData.name.trim(),
        defaultAmount: formData.defaultAmount ? parseFloat(formData.defaultAmount) : undefined,
        description: formData.description.trim() || undefined
      };

      let updatedHeaders;
      if (editingHeader) {
        updatedHeaders = headers.map(h => h.id === editingHeader.id ? newHeader : h);
      } else {
        updatedHeaders = [...headers, newHeader];
      }

      await updateDoc(doc(db, 'ServiceProviders', serviceProviderId), {
        paymentHeaders: updatedHeaders
      });

      setHeaders(updatedHeaders);
      setShowForm(false);
      setEditingHeader(null);
      setFormData({ name: '', defaultAmount: '', description: '' });
      
      toast.success(editingHeader ? "Category updated successfully!" : "Category added successfully!");
    } catch (error) {
      console.error('Error saving header:', error);
      toast.error('Failed to save category');
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  const handleEdit = (header: PaymentHeader) => {
    setEditingHeader(header);
    setFormData({
      name: header.name,
      defaultAmount: header.defaultAmount?.toString() || '',
      description: header.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (headerId: string) => {
    if (!confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      dispatch(toggleLoading(true));
      const updatedHeaders = headers.filter(h => h.id !== headerId);
      
      await updateDoc(doc(db, 'ServiceProviders', serviceProviderId), {
        paymentHeaders: updatedHeaders
      });

      setHeaders(updatedHeaders);
      toast.success("Category deleted successfully!");
    } catch (error) {
      console.error('Error deleting header:', error);
      toast.error('Failed to delete category');
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  const handleAddNew = () => {
    setEditingHeader(null);
    setFormData({ name: '', defaultAmount: '', description: '' });
    setShowForm(true);
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Manage Payment Categories
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create categories like "Food", "Welcome Kit", or custom categories. 
            Set default amounts that can be overridden when creating payment requests.
          </Typography>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            disabled={isLoading}
            sx={{ mb: 2 }}
          >
            Add New Category
          </Button>
        </Box>

        {/* Form for adding/editing headers */}
        {showForm && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {editingHeader ? 'Edit Category' : 'Add New Category'}
            </Typography>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    required
                    fullWidth
                    label="Category Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isLoading}
                    size="small"
                    placeholder="e.g., Food, Welcome Kit, Membership"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    label="Default Amount (Optional)"
                    name="defaultAmount"
                    type="number"
                    value={formData.defaultAmount}
                    onChange={handleChange}
                    disabled={isLoading}
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Description (Optional)"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    disabled={isLoading}
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Brief description of this category"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {isLoading ? 'Saving...' : (editingHeader ? 'Update' : 'Add Category')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setShowForm(false)}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )}

        {/* List of existing headers */}
        <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
          {headers.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No payment categories yet. Add your first category to get started!
              </Typography>
            </Box>
          ) : (
            <List>
              {headers.map((header, index) => (
                <React.Fragment key={header.id}>
                  <ListItem>
                    <ListItemText
                      primary={header.name}
                      secondary={
                        <Box>
                          {header.defaultAmount && (
                            <Typography variant="body2" color="text.secondary">
                              Default Amount: ₹{header.defaultAmount.toLocaleString()}
                            </Typography>
                          )}
                          {header.description && (
                            <Typography variant="body2" color="text.secondary">
                              {header.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleEdit(header)}
                        disabled={isLoading}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(header.id)}
                        disabled={isLoading}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < headers.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </DialogContent>
    </StyledDialog>
  );
} 